import './style.scss';
import { useAuth, useBasic } from 'contexts';
import { useCallback, useEffect, useState } from 'react';
import { getRanking, getInactivePlayers, getLeaderboards, BoardsInterface } from 'services';
import { formatPoints } from 'utils';
import * as XLSX from 'xlsx';

interface RankingItem {
  position: number;
  _id: string;
  name: string;
  total: number;
  image?: string;
  info: {
    extra: {
      company: string;
      departamento: string;
    };
  };
}

interface InactivePlayer {
  _id: string;
  name: string;
  email: string;
  image?: string;
  extra: {
    company: string;
    departamento: string;
  };
}

export default function RankingReportsPage() {
  const { appearance } = useBasic();
  const { profileUser } = useAuth();
  const [ranking, setRanking] = useState<RankingItem[]>([]);
  const [inactivePlayers, setInactivePlayers] = useState<InactivePlayer[]>([]);
  const [leaderboards, setLeaderboards] = useState<BoardsInterface[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');

  const isMenuActive = useCallback(
    (pathname: string) => {
      if (appearance?.menu && Object.keys(appearance?.menu as any).includes(pathname)) {
        return (appearance?.menu as any)[pathname];
      }
      return true;
    },
    [appearance],
  );

  useEffect(() => {
    if (profileUser?.roles?.includes('admin') === false) {
      window.location.href = '/';
    } else if (!isMenuActive('reports')) {
      window.location.href = '/';
    }
  }, [isMenuActive, profileUser]);

  useEffect(() => {
    const loadLeaderboards = async () => {
      try {
        const boardsData = await getLeaderboards();
        const activeBoards = boardsData.filter(x => !x.extra || !('active' in x.extra) || x.extra.active === true);
        activeBoards.sort((a, b) => {
          const orderA = a.extra?.order ?? 0;
          const orderB = b.extra?.order ?? 0;
          return orderA - orderB;
        });
        setLeaderboards(activeBoards);
        if (activeBoards.length > 0 && !selectedId) {
          setSelectedId(activeBoards[0]._id);
        }
      } catch (error) {
        console.log(error);
      }
    };
    loadLeaderboards();
  }, [selectedId]);

  useEffect(() => {
    const loadInactive = async () => {
      try {
        const inactiveData = await getInactivePlayers();
        setInactivePlayers(inactiveData);
      } catch (error) {
        console.log(error);
      }
    };
    loadInactive();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const loadRanking = async () => {
      try {
        const rankingData = await getRanking(selectedId);
        setRanking(rankingData.map((item, index) => ({ ...item, position: index + 1 })));
      } catch (error) {
        console.log(error);
      }
    };
    loadRanking();
  }, [selectedId]);

  const handleExportRanking = useCallback(async () => {
    try {
      const rankingData = await getRanking(selectedId);
      const dataWithPosition = rankingData.map((item, index) => ({
        Posição: index + 1,
        Nome: item.name,
        Pontos: formatPoints(item.total),
        Empresa: item.info.extra.company,
        Departamento: item.info.extra.departamento,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataWithPosition);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ranking');
      XLSX.writeFile(workbook, 'ranking.xlsx');
    } catch (error) {
      console.log(error);
    }
  }, [selectedId]);

  const handleExportInactive = useCallback(async () => {
    try {
      const inactiveData = await getInactivePlayers();
      const dataFormatted = inactiveData.map(item => ({
        Nome: item.name,
        Email: item.email,
        Empresa: item.extra.company,
        Departamento: item.extra.departamento,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataFormatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Jogadores Inativos');
      XLSX.writeFile(workbook, 'jogadores_inativos.xlsx');
    } catch (error) {
      console.log(error);
    }
  }, []);

  return (
    <div className="p-4">
      <select className="mb-4 w-full p-2 border rounded" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
        {leaderboards.map(board => (
          <option key={board._id} value={board._id}>
            {board.title}
          </option>
        ))}
      </select>

      <h1 className="text-2xl font-bold mb-4">Ranking</h1>
      {ranking.length > 0 && (
        <div className="text-right">
          <button onClick={handleExportRanking} className="bg-green-500 text-white px-4 py-2 rounded mb-4">
            Exportar Ranking
          </button>
        </div>
      )}

      <div className="bg-white p-4 rounded shadow mb-8">
        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-center">POSIÇÃO</th>
              <th className="p-2"></th>
              <th className="p-2">NOME</th>
              <th className="p-2 text-right">PONTOS</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((obj, index) => (
              <tr key={index} className="border-t">
                <td className="p-2 text-center text-yellow-600 text-lg font-bold">{obj.position}º</td>
                <td className="p-2">
                  <img
                    src={obj.image || 'https://my.funifier.com/images/no-image.png'}
                    alt=""
                    className="w-12 h-12 object-cover rounded"
                  />
                </td>
                <td className="p-2">
                  <strong className="text-yellow-600">{obj.name}</strong>
                  <br />
                  <small>
                    {obj.info.extra.company} | {obj.info.extra.departamento}
                  </small>
                </td>
                <td className="p-2 text-right text-2xl">{formatPoints(obj.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {ranking.length === 0 && <p className="text-center text-gray-500 m-4">Nenhum dado encontrado.</p>}
      </div>

      <h3 className="text-xl font-bold mb-4">Jogadores que ainda não participaram</h3>
      {inactivePlayers.length > 0 && (
        <div className="text-right">
          <button onClick={handleExportInactive} className="bg-green-500 text-white px-4 py-2 rounded mb-4">
            Exportar Inativos
          </button>
        </div>
      )}

      <div className="bg-white p-4 rounded shadow">
        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2"></th>
              <th className="p-2">NOME</th>
              <th className="p-2">E-MAIL</th>
              <th className="p-2">EMPRESA</th>
              <th className="p-2">DEPARTAMENTO</th>
            </tr>
          </thead>
          <tbody>
            {inactivePlayers.map((obj, index) => (
              <tr key={index} className="border-t">
                <td className="p-2">
                  <img
                    src={obj.image || 'https://my.funifier.com/images/no-image.png'}
                    alt=""
                    className="w-12 h-12 object-cover rounded"
                  />
                </td>
                <td className="p-2">
                  <strong className="text-yellow-600">{obj.name}</strong>
                </td>
                <td className="p-2">{obj.email}</td>
                <td className="p-2">{obj.extra.company}</td>
                <td className="p-2">{obj.extra.departamento}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {inactivePlayers.length === 0 && <p className="text-center text-gray-500 m-4">Nenhum dado encontrado.</p>}
      </div>
    </div>
  );
}
